import React, { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import DriveConnectButton from "./components/DriveConnectButton";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Toast from "../shared/components/Toast";
import FAQSection from "./components/FAQSection";
import CaseStatusCard from "./components/CaseStatusCard";
import UploadOptions from "./components/UploadOptions";
import LoanApplicationForm from "./components/LoanApplicationForm";
// import { useSession } from "../shared/hooks/useSession"; // Disabled for debugging
import { safeRedirect } from "../shared/utils/secureRedirect";
import { jwtDecode } from "jwt-decode";
import { getTokens, clearTokens } from "../shared/utils/secureTokenStorage";
import { fetchWithAuth, BASE_URL } from "../lender/api/client";
import { uploadCaseDocuments } from "../lender/api/cases";
import { validateFiles, sanitizeFile } from "../shared/utils/fileValidation";

export default function App() {
  const [user, setUser] = useState(null);
  const [mode, setMode] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [error, setError] = useState(null);
  const [caseNumber, setCaseNumber] = useState("");
  const [caseInfo, setCaseInfo] = useState(null);
  const [isCaseLoading, setIsCaseLoading] = useState(false);
  const [caseLookupError, setCaseLookupError] = useState("");
  const [toast, setToast] = useState(null);
  const [loanApplicationComplete, setLoanApplicationComplete] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [hasExistingLoanApplication, setHasExistingLoanApplication] = useState(false);
  const [isCompletingCase, setIsCompletingCase] = useState(false);
  const [documentsUploaded, setDocumentsUploaded] = useState(false);

  // Dynamic steps based on whether loan application already exists
  // If exists: Enter Case (1) → Upload Documents (2) → Complete (3)
  // If not: Enter Case (1) → Loan Application (2) → Upload Documents (3) → Complete (4)
  const getSteps = () => {
    if (hasExistingLoanApplication) {
      return [
        { step: 1, label: "Enter Case" },
        { step: 2, label: "Upload Documents" },
        { step: 3, label: "Complete" }
      ];
    }
    return [
      { step: 1, label: "Enter Case" },
      { step: 2, label: "Loan Application" },
      { step: 3, label: "Upload Documents" },
      { step: 4, label: "Complete" }
    ];
  };

  // Calculate current step based on flow type
  const getCurrentStep = () => {
    if (uploadComplete) return hasExistingLoanApplication ? 3 : 4;
    if (documentsUploaded || (loanApplicationComplete && !uploadComplete)) return hasExistingLoanApplication ? 2 : 3;
    if (caseInfo && hasExistingLoanApplication) return 2; // Skip to upload
    if (caseInfo) return 2; // Loan application step
    return 1; // Enter case
  };

  const currentStep = getCurrentStep();
  const steps = getSteps();

  // Session management disabled temporarily for debugging
  // useSession(user, () => {
  //   setToast({ message: "Session expired. Please sign in again.", type: "warning" });
  //   handleSignOut();
  // });

  const handleCaseLookup = async (event) => {
    event.preventDefault();
    const trimmed = caseNumber.trim();
    if (!trimmed) {
      setCaseLookupError("Enter the case number to continue.");
      return;
    }
    setCaseLookupError("");
    setIsCaseLoading(true);
    try {
      const apiUrl = `${BASE_URL}/api/cases/by-number?caseNumber=${encodeURIComponent(trimmed)}`;

      // DEBUG: Log the API request
      console.log('[Borrower] Case lookup request:', {
        BASE_URL,
        fullUrl: apiUrl,
        caseNumber: trimmed
      });

      // Use fetchWithAuth to include JWT token if available
      const response = await fetchWithAuth(apiUrl, {
        headers: { accept: "application/json" }
      });

      // DEBUG: Log the response
      console.log('[Borrower] Case lookup response:', {
        status: response.status,
        ok: response.ok
      });

      if (!response.ok) {
        throw new Error("Unable to find that case. Please check the case number and try again.");
      }
      const data = await response.json();
      const payload = data?.data || data;
      setCaseInfo(payload);

      if (!user && (payload?.email || payload?.borrowerEmail || payload?.name)) {
        setUser({
          email: payload.email || payload.borrowerEmail || "",
          name: payload.name || ""
        });
      }

      // Check if loan application already exists for this case
      try {
        const caseId = payload?.id || payload?.caseId;
        const loanAppUrl = `${BASE_URL}/api/loan-applications/case/${caseId}`;

        // DEBUG: Log the loan app check request
        console.log('[Borrower] Loan app check request:', {
          BASE_URL,
          fullUrl: loanAppUrl,
          caseId
        });

        // Use fetchWithAuth to include JWT token if available
        const loanAppResponse = await fetchWithAuth(
          loanAppUrl,
          { headers: { accept: "application/json" } }
        );

        // DEBUG: Log the loan app check response
        console.log('[Borrower] Loan app check response:', {
          status: loanAppResponse.status,
          ok: loanAppResponse.ok
        });

        if (loanAppResponse.ok) {
          const loanAppData = await loanAppResponse.json();
          // Check if loan application exists (data is not null and has actual content)
          const hasLoanApp = loanAppData?.data !== null && (loanAppData?.data?.id || loanAppData?.id);
          if (hasLoanApp) {
            // Loan application already exists - skip that step
            setHasExistingLoanApplication(true);
            setLoanApplicationComplete(true);
            setToast({
              message: "Welcome back! Your loan application is on file. Please upload any additional documents.",
              type: "info"
            });
          } else {
            // No loan application - borrower needs to fill it out
            setHasExistingLoanApplication(false);
          }
        }
      } catch (loanAppError) {
        // No existing loan application - borrower needs to fill it out
        console.log("No existing loan application found - borrower will complete URLA");
        setHasExistingLoanApplication(false);
      }
    } catch (lookupError) {
      console.error("❌ Case lookup error:", lookupError);
      setCaseLookupError("Unable to find that case. Please check the case number and try again.");
      setCaseInfo(null);
    } finally {
      setIsCaseLoading(false);
    }
  };

  const handleLoanApplicationComplete = (applicationData) => {
    console.log("Loan application submitted:", applicationData);
    setLoanApplicationComplete(true);
    setToast({ message: "Loan application submitted successfully!", type: "success" });
  };

  const handleSignOut = () => {
    setUser(null);
    setMode(null);
    setSelectedFolder(null);
    setError(null);
    setCaseInfo(null);
    setLoanApplicationComplete(false);
    setUploadComplete(false);
    setHasExistingLoanApplication(false);
    setDocumentsUploaded(false);
    setIsCompletingCase(false);
    sessionStorage.clear();
    localStorage.clear();
    clearTokens();
    safeRedirect("/");
  };

  const handleLogoClick = () => {
    handleSignOut();
    safeRedirect("/");
  };

  const handleManualUpload = async (event) => {
    if (!caseInfo || !event.target.files?.length) return;
    const files = Array.from(event.target.files);
    event.target.value = "";

    const maxSize = 25 * 1024 * 1024;
    const tooLarge = files.find(f => f.size > maxSize);
    if (tooLarge) {
      setToast({ message: "File exceeds 25 MB limit.", type: "error" });
      return;
    }

    const validation = validateFiles(files);
    if (!validation.valid) {
      setToast({ message: "Upload failed: Invalid file type.", type: "error" });
      return;
    }

    const sanitizedFiles = validation.validFiles.map(sanitizeFile);
    setToast({ message: `Uploading ${sanitizedFiles.length} file(s)...`, type: "info" });

    const toPayload = (file) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = typeof reader.result === 'string' ? reader.result.split(',').pop() : '';
        resolve({
          name: file.name,
          size: file.size,
          type: file.type || 'application/octet-stream',
          content: base64,
          source: "Borrower Upload"
        });
      };
      reader.onerror = () => reject(new Error("File read error"));
      reader.readAsDataURL(file);
    });

    try {
      const filePayloads = await Promise.all(sanitizedFiles.map(toPayload));
      await uploadCaseDocuments({
        caseId: caseInfo.id || caseInfo.caseId,
        files: filePayloads
      });
      setDocumentsUploaded(true);
      setToast({ message: "Documents uploaded successfully! Click 'Complete & Notify Lender' when ready.", type: "success" });
    } catch (error) {
      console.error("Upload error", error);
      setToast({ message: "Unable to upload documents. Please try again.", type: "error" });
    }
  };

  // Handle completing the case and notifying lender
  const handleCompleteCase = async () => {
    setIsCompletingCase(true);
    try {
      const caseId = caseInfo?.id || caseInfo?.caseId;

      // Notify the lender by updating case status
      const response = await fetchWithAuth(`${BASE_URL}/api/cases/${caseId}/borrower-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: 'Borrower has completed document upload and is ready for review.',
          completedAt: new Date().toISOString()
        })
      });

      // Even if API doesn't exist yet, mark as complete locally
      setUploadComplete(true);
      setToast({ message: "Your lender has been notified. Thank you!", type: "success" });
    } catch (error) {
      console.error("Error notifying lender:", error);
      // Still mark as complete for the user
      setUploadComplete(true);
      setToast({ message: "Submission complete! Your lender will be in touch soon.", type: "success" });
    } finally {
      setIsCompletingCase(false);
    }
  };

  // Load user from token on mount only (not on user change to avoid loops)
  useEffect(() => {
    const loadUserFromToken = () => {
      try {
        const tokens = getTokens();
        if (tokens?.accessToken) {
          const decoded = jwtDecode(tokens.accessToken);
          const emailFromToken = decoded?.email || decoded?.user || decoded?.sub;
          if (emailFromToken) {
            setUser({ email: emailFromToken });
          }
        }
      } catch (err) {
        console.log('No valid token found, user will enter case number to continue');
      }
    };

    loadUserFromToken();
  }, []); // Only run once on mount

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <Header
        onLogoClick={handleLogoClick}
        userEmail={user?.email || user?.name || caseInfo?.email || caseInfo?.borrowerEmail}
        onSignOut={handleSignOut}
      />
      <div
        style={{
          minHeight: "100vh",
          background:
            "radial-gradient(circle at 15% 20%, rgba(14,165,233,0.18), transparent 42%), radial-gradient(circle at 85% 15%, rgba(99,102,241,0.16), transparent 40%), linear-gradient(135deg, #0b1220 0%, #0f172a 55%, #0b1220 100%)",
          fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          padding: "100px 10px 140px",
        }}
      >
        <div style={{ maxWidth: "960px", margin: "0 auto 40px auto", textAlign: "center" }}>
          <h1
            style={{
              margin: "16px 0 12px 0",
              fontSize: "52px",
              lineHeight: 1.1,
              fontWeight: 900,
              color: "#ffffff",
            }}
          >
            Welcome to the new era
            <br />
            <span
              style={{
                background: "linear-gradient(90deg, #38bdf8, #818cf8)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                display: "inline-block",
                paddingBottom: "4px",
              }}
            >
              of home financing.
            </span>
          </h1>
        </div>

        {/* Progress Steps - Dynamic based on whether loan application exists */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0",
          marginBottom: "32px",
          maxWidth: "700px",
          margin: "0 auto 32px auto"
        }}>
          {steps.map(({ step, label }, index) => (
            <React.Fragment key={step}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                <div style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: step <= currentStep ? "linear-gradient(135deg, #38bdf8, #818cf8)" : "rgba(148, 163, 184, 0.3)",
                  color: step <= currentStep ? "#fff" : "#94a3b8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  fontSize: "14px",
                  boxShadow: step === currentStep ? "0 0 20px rgba(56, 189, 248, 0.6)" : "none",
                  transition: "all 0.3s ease"
                }}>
                  {step < currentStep ? "✓" : step}
                </div>
                <span style={{
                  fontSize: "12px",
                  color: step <= currentStep ? "#ffffff" : "#94a3b8",
                  fontWeight: step === currentStep ? 600 : 400,
                  whiteSpace: "nowrap"
                }}>
                  {label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div style={{
                  width: "60px",
                  height: "2px",
                  background: step < currentStep ? "linear-gradient(90deg, #38bdf8, #6366f1)" : "rgba(148, 163, 184, 0.2)",
                  marginBottom: "24px",
                  transition: "all 0.3s ease"
                }} />
              )}
            </React.Fragment>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
          style={{
            width: caseInfo && !loanApplicationComplete ? "780px" : "640px",
            maxWidth: "95%",
            margin: "0 auto",
            padding: "44px",
            borderRadius: "20px",
            background: "linear-gradient(145deg, rgba(15,23,42,0.94), rgba(12,18,32,0.9))",
            boxShadow: "0 24px 80px rgba(8,12,24,0.55), inset 0 0 0 1px rgba(255,255,255,0.05)",
            backdropFilter: "blur(18px)",
            border: "1px solid rgba(59,130,246,0.18)",
            textAlign: caseInfo && !loanApplicationComplete ? "left" : "center",
            position: "relative",
            transition: "width 0.3s ease",
          }}
        >
          {/* Top right: Back + Sign Out */}
          {user && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "10px",
                marginBottom: "12px",
              }}
            >
              {mode && (
                <button
                  onClick={() => setMode(null)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "#cbd5e1",
                    background: "none",
                    border: "1px solid rgba(59,130,246,0.4)",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: 600,
                    padding: "6px 10px",
                    borderRadius: "10px",
                    transition: "all 0.2s",
                    backdropFilter: "blur(6px)",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(59,130,246,0.14)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <ArrowLeft size={14} style={{ marginRight: 6 }} /> Back
                </button>
              )}
            </div>
          )}

          {!caseInfo && (
            <>
              {/* Video hidden for now - uncomment to restore
              <div
                style={{
                  marginBottom: "28px",
                  borderRadius: "16px",
                  overflow: "hidden",
                  border: "1px solid rgba(148,163,184,0.25)",
                  boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
                  background: "rgba(15,23,42,0.8)",
                }}
              >
                <div
                  style={{
                    padding: "14px 18px",
                    borderBottom: "1px solid rgba(148,163,184,0.2)",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    color: "#e2e8f0",
                    fontWeight: 700,
                    fontSize: "15px",
                  }}
                >
                  <CloudUpload size={18} />
                  Let's Get Started
                </div>
                <div
                  style={{
                    position: "relative",
                    paddingBottom: "56.25%",
                    height: 0,
                    overflow: "hidden",
                    background: "#000",
                  }}
                >
                  <iframe
                    title="Borrower onboarding video"
                    src="https://www.youtube.com/embed/yT6omjC-NKo"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                  />
                </div>
              </div>
              */}

              <div style={{ marginBottom: 28 }}>
                <p
                  style={{
                    margin: "0 0 12px 0",
                    fontSize: "15px",
                    color: "#cbd5e1",
                    fontWeight: 600,
                  }}
                >
                  Enter your case number to continue
                </p>
                <form
                  onSubmit={handleCaseLookup}
                  style={{ display: "flex", gap: "10px", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}
                >
                  <input
                    type="text"
                    value={caseNumber}
                    onChange={(e) => setCaseNumber(e.target.value)}
                    placeholder="Check your email for your case number"
                    style={{
                      flex: 1,
                      minWidth: "240px",
                      padding: "12px 14px",
                      borderRadius: "12px",
                      border: "1px solid rgba(148,163,184,0.4)",
                      background: "rgba(255,255,255,0.04)",
                      color: "#e2e8f0",
                      outline: "none",
                    }}
                  />
                  <button
                    type="submit"
                    disabled={isCaseLoading}
                    style={{
                      padding: "12px 16px",
                      borderRadius: "12px",
                      border: "none",
                      background: "linear-gradient(135deg, #0ea5e9, #6366f1)",
                      color: "white",
                      fontWeight: 700,
                      minWidth: "110px",
                      cursor: "pointer",
                      boxShadow: "0 12px 40px rgba(14,165,233,0.3)",
                    }}
                  >
                    {isCaseLoading ? "Checking..." : "Continue"}
                  </button>
                </form>
                {caseLookupError && (
                  <p style={{ color: "#fca5a5", fontSize: "13px", marginTop: "10px" }}>{caseLookupError}</p>
                )}
              </div>

              <FAQSection />
            </>
          )}

          <CaseStatusCard caseInfo={caseInfo} />

          {/* Returning user message - show when loan application exists */}
          {caseInfo && hasExistingLoanApplication && !uploadComplete && (
            <div style={{
              padding: "16px 20px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, rgba(56, 189, 248, 0.1), rgba(99, 102, 241, 0.05))",
              border: "1px solid rgba(56, 189, 248, 0.2)",
              marginBottom: "24px",
              display: "flex",
              alignItems: "center",
              gap: "12px"
            }}>
              <div style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #22c55e, #16a34a)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0
              }}>
                <span style={{ color: "#fff", fontSize: "18px" }}>✓</span>
              </div>
              <div>
                <p style={{ margin: 0, color: "#e2e8f0", fontWeight: 600, fontSize: "14px" }}>
                  Loan Application Already Submitted
                </p>
                <p style={{ margin: "4px 0 0", color: "#94a3b8", fontSize: "13px" }}>
                  Your URLA is on file. You can upload additional documents below.
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Loan Application Form - Only for new users */}
          {caseInfo && !loanApplicationComplete && !hasExistingLoanApplication && (
            <LoanApplicationForm
              caseInfo={caseInfo}
              onComplete={handleLoanApplicationComplete}
              onError={(message) => setToast({ message, type: "error" })}
            />
          )}

          {/* Step 3: Document Upload */}
          {caseInfo && loanApplicationComplete && !uploadComplete && (
            <>
              <div style={{ margin: "24px 0 36px" }}>
                <h2
                  style={{
                    margin: "0 0 8px 0",
                    fontWeight: 800,
                    fontSize: "32px",
                    background: "linear-gradient(135deg, #38bdf8 0%, #6366f1 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {selectedFolder ? "Final Step: Share Documents" : "Upload Your Documents"}
                </h2>
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "#cbd5e1",
                    fontWeight: 500,
                  }}
                >
                  {selectedFolder
                    ? "Confirm the selected folder is correct and click buttons below to finish."
                    : "Please upload the following documents to complete your application."}
                </p>
              </div>

              {/* Tutorial Video / Checklist */}
              <AnimatePresence mode="wait">
                {!mode ? (
                  <motion.div
                    key="checklist"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                  >
                    <ul
                      style={{
                        textAlign: "left",
                        color: "#cbd5e1",
                        fontSize: "13px",
                        marginBottom: "25px",
                        lineHeight: 1.6,
                      }}
                    >
                      <li> Government-issued ID (Driver's License or Passport)</li>
                      <li> Recent Pay Stubs (last 30–60 days)</li>
                      <li> Last 2 years of W-2s and Tax Returns</li>
                      <li> Recent Bank Statements (2–3 months)</li>
                      <li> Mortgage or Loan Statements (if applicable)</li>
                      <li> Proof of Assets (401k, brokerage, savings)</li>
                      <li> Property Insurance and Tax Bills (if owned)</li>
                    </ul>

                    <p
                      style={{
                        color: "#cbd5e1",
                        fontSize: "14px",
                        marginBottom: "20px",
                        lineHeight: 1.5,
                      }}
                    >
                      Once ready, click the button below to upload them securely.
                    </p>

                    <UploadOptions
                      onConnectDrive={() => setMode("drive")}
                      onManualUpload={handleManualUpload}
                      showDriveOption={false}  /* Set to true to re-enable Google Drive */
                    />

                    {/* Complete & Notify Lender Button - shows after documents uploaded */}
                    {documentsUploaded && (
                      <div style={{ marginTop: "32px", textAlign: "center" }}>
                        <div style={{
                          padding: "16px",
                          borderRadius: "12px",
                          background: "rgba(34, 197, 94, 0.1)",
                          border: "1px solid rgba(34, 197, 94, 0.3)",
                          marginBottom: "20px"
                        }}>
                          <p style={{ margin: 0, color: "#4ade80", fontSize: "14px", fontWeight: 500 }}>
                            ✓ Documents uploaded successfully
                          </p>
                          <p style={{ margin: "8px 0 0", color: "#94a3b8", fontSize: "13px" }}>
                            You can upload more documents or click below to notify your lender.
                          </p>
                        </div>
                        <button
                          onClick={handleCompleteCase}
                          disabled={isCompletingCase}
                          style={{
                            padding: "16px 32px",
                            borderRadius: "12px",
                            border: "none",
                            background: "linear-gradient(135deg, #22c55e, #16a34a)",
                            color: "#fff",
                            fontSize: "16px",
                            fontWeight: 700,
                            cursor: isCompletingCase ? "wait" : "pointer",
                            boxShadow: "0 8px 32px rgba(34, 197, 94, 0.3)",
                            opacity: isCompletingCase ? 0.7 : 1,
                            transition: "all 0.2s ease"
                          }}
                        >
                          {isCompletingCase ? "Notifying Lender..." : "Complete & Notify Lender"}
                        </button>
                      </div>
                    )}

                  </motion.div>
                ) : (
                  <motion.div
                    key="upload"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                  >
                    {mode === "drive" && (
                      <DriveConnectButton
                        autoStart
                        caseId={caseInfo?.caseId || caseInfo?.id || ""}
                        onFolderSelect={(folderId) => setSelectedFolder(folderId)}
                        onError={(message) => setToast({ message, type: "error" })}
                      />
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}

          {/* Step 4: Complete */}
          {uploadComplete && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              style={{ textAlign: "center", padding: "40px 20px" }}
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #22c55e, #16a34a)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 24px",
                  boxShadow: "0 8px 32px rgba(34, 197, 94, 0.3)",
                }}
              >
                <span style={{ fontSize: "36px" }}>✓</span>
              </div>
              <h2
                style={{
                  margin: "0 0 12px",
                  fontSize: "28px",
                  fontWeight: 700,
                  color: "#e2e8f0",
                }}
              >
                Application Complete!
              </h2>
              <p style={{ margin: 0, color: "#94a3b8", fontSize: "15px", lineHeight: 1.6 }}>
                Thank you for submitting your loan application and documents.
                <br />
                Our team will review your application and contact you shortly.
              </p>
            </motion.div>
          )}

          {error && (
            <p style={{ color: "red", fontSize: "13px", marginTop: "10px" }}>{error}</p>
          )}
        </motion.div>
      </div>
      <Footer />
    </>
  );
}
